import { apiFetch, apiFetchAbsolute } from '../../../api/axiosConfig';

function toQueryString(/* istanbul ignore next -- 全呼び出し元が params を渡すため既定値は使われない */ params = {}) {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			searchParams.append(key, value);
		}
	});
	const query = searchParams.toString();
	return query ? `?${query}` : '';
}

function extractList(payload) {
	const data = payload?.data;
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.results)) return data.results;
	return [];
}

async function fetchAllPages(pathWithQuery) {
	const separator = pathWithQuery.includes('?') ? '&' : '?';
	const url = pathWithQuery.includes('page_size')
		? pathWithQuery
		: `${pathWithQuery}${separator}page_size=10000`;

	const all = [];
	let payload = await apiFetch(url);
	all.push(...extractList(payload));

	let next = payload?.data?.next;
	while (next) {
		payload = await apiFetchAbsolute(next);
		all.push(...extractList(payload));
		next = payload?.data?.next;
	}

	return all;
}

export async function getInitData() {
	const response = await apiFetch('/init_data/');
	return response?.data;
}

export async function getProgressSchedules(params = {}) {
	return fetchAllPages(`/progress_schedules/${toQueryString(params)}`);
}

export async function getProgressSchedule(id) {
	const response = await apiFetch(`/progress_schedules/${id}/`);
	return response?.data;
}

export async function getScheduleChart(params = {}) {
	const response = await apiFetch(`/progress_schedules/schedule_chart/${toQueryString(params)}`);
	return response?.data;
}

export async function getScheduleOemRegionPairs() {
	const response = await apiFetch('/progress_schedules/oem_region_pairs/');
	return response?.data || [];
}

// 成果物管理から進捗管理へリレーションを張るための選択肢
// （OEM×対地・カテゴリ・大中小項目・Phase の組み合わせと、その進捗管理ID）
export async function getDeliverableLinkTargets() {
	const response = await apiFetch('/progress_schedules/deliverable_link_targets/');
	return response?.data || [];
}

export async function getProgressReports(params = {}) {
	return fetchAllPages(`/progress_reports/${toQueryString(params)}`);
}

export async function getProblems(params = {}) {
	return fetchAllPages(`/problems/${toQueryString(params)}`);
}

export async function getRisks(params = {}) {
	return fetchAllPages(`/risks/${toQueryString(params)}`);
}

export async function getOems(params = {}) {
	return fetchAllPages(`/oems/${toQueryString(params)}`);
}

export async function getRegions(params = {}) {
	return fetchAllPages(`/regions/${toQueryString(params)}`);
}

export async function getCategories(params = {}) {
	return fetchAllPages(`/categories/${toQueryString(params)}`);
}

export async function getMiddleCategories(params = {}) {
	return fetchAllPages(`/middle_categories/${toQueryString(params)}`);
}

export async function getLargeCategories(params = {}) {
	return fetchAllPages(`/large_categories/${toQueryString(params)}`);
}

export async function getSmallCategories(params = {}) {
	return fetchAllPages(`/small_categories/${toQueryString(params)}`);
}

export async function getDepartments(params = {}) {
	return fetchAllPages(`/departments/${toQueryString(params)}`);
}

export async function getActors(params = {}) {
	return fetchAllPages(`/actors/${toQueryString(params)}`);
}

export async function getStatuses(params = {}) {
	return fetchAllPages(`/statuses/${toQueryString(params)}`);
}

export async function getProbabilities(params = {}) {
	return fetchAllPages(`/probabilities/${toQueryString(params)}`);
}

export async function getPolicies(params = {}) {
	return fetchAllPages(`/policies/${toQueryString(params)}`);
}

export async function getCauses(params = {}) {
	return fetchAllPages(`/causes/${toQueryString(params)}`);
}

export async function getImpacts(params = {}) {
	return fetchAllPages(`/impacts/${toQueryString(params)}`);
}

export async function getPhases(params = {}) {
	return fetchAllPages(`/phases/${toQueryString(params)}`);
}

export async function getOemRegions(params = {}) {
	return fetchAllPages(`/oem_regions/${toQueryString(params)}`);
}

export async function getScheduleTemplates(params = {}) {
	return fetchAllPages(`/schedule_templates/${toQueryString(params)}`);
}

export async function getEmployees(params = {}) {
	return fetchAllPages(`/employees/${toQueryString(params)}`);
}

export async function getMasterItems(type, params = {}) {
	return fetchAllPages(`/${type}/${toQueryString(params)}`);
}

export async function createMasterItem(type, payload) {
	const response = await apiFetch(`/${type}/`, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateMasterItem(type, id, payload) {
	const response = await apiFetch(`/${type}/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteMasterItem(type, id) {
	const response = await apiFetch(`/${type}/${id}/`, {
		method: 'DELETE',
	});
	return response?.data;
}

export async function validateMasterBulk(type, rows) {
	const response = await apiFetch(`/${type}/bulk_validate/`, {
		method: 'POST',
		body: JSON.stringify({ rows }),
	});
	return response?.data;
}

export async function importMasterBulk(type, rows) {
	const response = await apiFetch(`/${type}/bulk_import/`, {
		method: 'POST',
		body: JSON.stringify({ rows }),
	});
	return response?.data;
}

export async function generateFromTemplate({ oem_id, region_id, template_ids, phase_map }) {
	const response = await apiFetch('/progress_schedules/generate_from_template/', {
		method: 'POST',
		body: JSON.stringify({ oem_id, region_id, template_ids, phase_map }),
	});
	return response?.data;
}

export async function createProgressSchedule(payload) {
	const response = await apiFetch('/progress_schedules/', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateProgressSchedule(id, payload) {
	const response = await apiFetch(`/progress_schedules/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteProgressSchedule(id, updatedAtIso = null) {
	await apiFetch(`/progress_schedules/${id}/`, {
		method: 'DELETE',
		body: JSON.stringify({ updated_at_iso: updatedAtIso }),
	});
}

export async function createProgressReport(payload) {
	const response = await apiFetch('/progress_reports/', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateProgressReport(id, payload) {
	const response = await apiFetch(`/progress_reports/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteProgressReport(id, updatedAtIso = null) {
	await apiFetch(`/progress_reports/${id}/`, {
		method: 'DELETE',
		body: JSON.stringify({ updated_at_iso: updatedAtIso }),
	});
}

export async function createProblem(payload) {
	const response = await apiFetch('/problems/', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateProblem(id, payload) {
	const response = await apiFetch(`/problems/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteProblem(id, updatedAtIso = null) {
	await apiFetch(`/problems/${id}/`, {
		method: 'DELETE',
		body: JSON.stringify({ updated_at_iso: updatedAtIso }),
	});
}

export async function createRisk(payload) {
	const response = await apiFetch('/risks/', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateRisk(id, payload) {
	const response = await apiFetch(`/risks/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteRisk(id, updatedAtIso = null) {
	await apiFetch(`/risks/${id}/`, {
		method: 'DELETE',
		body: JSON.stringify({ updated_at_iso: updatedAtIso }),
	});
}

export async function getAiManagements(params = {}) {
	return fetchAllPages(`/ai_managements/${toQueryString(params)}`);
}

export async function createAiManagement(payload) {
	const response = await apiFetch('/ai_managements/', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateAiManagement(id, payload) {
	const response = await apiFetch(`/ai_managements/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteAiManagement(id, updatedAtIso = null) {
	await apiFetch(`/ai_managements/${id}/`, {
		method: 'DELETE',
		body: JSON.stringify({ updated_at_iso: updatedAtIso }),
	});
}

export async function getDeliverableManagements(params = {}) {
	return fetchAllPages(`/deliverable_managements/${toQueryString(params)}`);
}

export async function createDeliverableManagement(payload) {
	const response = await apiFetch('/deliverable_managements/', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function updateDeliverableManagement(id, payload) {
	const response = await apiFetch(`/deliverable_managements/${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(payload),
	});
	return response?.data;
}

export async function deleteDeliverableManagement(id, updatedAtIso = null) {
	await apiFetch(`/deliverable_managements/${id}/`, {
		method: 'DELETE',
		body: JSON.stringify({ updated_at_iso: updatedAtIso }),
	});
}

// ================================================================
// 推移分析 API
// ================================================================

export async function getIssueRiskTransitions() {
	const response = await apiFetch('/risk_stats/transitions/');
	return response?.data;
}

// ================================================================
// 権限管理 API
// ================================================================

export async function getPermissionManagements() {
	const payload = await apiFetch('/permission_managements/');
	return payload?.data || [];
}

export async function getMyPermissions() {
	const payload = await apiFetch('/permission_managements/me/');
	if (payload?.user_name) {
		sessionStorage.setItem('mobibi_user_name', payload.user_name);
	}
	return payload?.data || [];
}

// ================================================================
// Notion同期 API
// ================================================================

export async function triggerNotionSync() {
	const response = await apiFetch('/notion_sync/', {
		method: 'POST',
		body: JSON.stringify({}),
	});
	return response;
}

export async function updatePermissionManagement(id, permissionId) {
	const payload = await apiFetch(`/permission_managements/${id}/`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ permission_id: permissionId }),
	});
	return payload?.data;
}

/**
 * ローカル開発用ログイン（ALLOW_ANONYMOUS_LOCAL=True の場合のみ有効）
 */
export async function localLogin(employeeId, password) {
	const payload = await apiFetch('/local_login/', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ employee_id: employeeId, password }),
	});
	return payload;
}

/**
 * 開発者ログイン（/system-admin 画面用）
 * MS(Cognito)を介さず ID+パスワードで認証する。
 * DJANGO_ALLOW_DEV_LOGIN=True の環境（検証・本番）でのみ有効。
 *
 * 注意: 共通の apiFetch は使わない。ログイン失敗(401)を共通ハンドラが
 * 「セッション切れ」とみなして MS ログインへリダイレクトしてしまうため、
 * ここでは専用の fetch でエラーをそのまま呼び出し元（フォーム）に返す。
 */
export async function devLogin(employeeId, password) {
	const base = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1/dashboard';
	const response = await fetch(`${base}/dev_login/`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ employee_id: employeeId, password }),
	});
	const text = await response.text();
	let payload = null;
	try {
		payload = text ? JSON.parse(text) : null;
	} catch (parseError) {
		payload = null;
	}
	if (!response.ok) {
		const message =
			(payload && (payload.message || payload.detail)) ||
			'社員番号またはパスワードが正しくありません';
		const err = new Error(message);
		err.status = response.status;
		throw err;
	}
	return payload;
}
